/* eslint-disable no-underscore-dangle */
import { Constants } from '@pwc/pwc-deals-web-common';
import { syncedInfoWrapped } from 'common/components/Autosync/SyncedInfo.tsx';
import { junctionColorMapping } from 'common/helpers/junctionColorMapping';
import { get } from 'lodash';
import { stringifyUnableToLoadContent } from 'features/analyses/common/UnableToLoadContent/UnableToLoadContent';
import {
  getExcelLibraryItemsByGroup,
  getLibraryItemForStructuredContent,
  getPptLibraryItemsByGroup,
} from '../../features/contentLibrary/libraryItemService';

async function fetchItemAndAutosync(projectId, request) {
  const item = await getExcelLibraryItemsByGroup(projectId, request);
  const autoSync = get(item, ['fileContents', 0, 'autoSync']);
  return { item, autoSync };
}

export const getRichTextLongDescription = async (
  projectId,
  documentCode,
  contentId,
  contentVersionNumber,
  adjustmentRowId,
  libraryItemLinks = [],
  isClientView,
  isPwcUser
) => {
  try {
    if (libraryItemLinks.length === 0) {
      return null;
    }

    const matchingLibraryItemLink = libraryItemLinks.find(
      (link) => link.textKey === adjustmentRowId
    );

    if (!matchingLibraryItemLink) {
      return null;
    }

    const libraryItem = await getLibraryItemForStructuredContent(
      projectId,
      matchingLibraryItemLink.libraryItemId,
      contentId,
      contentVersionNumber,
      isClientView
    );

    let request;
    if (libraryItem === null) {
      return null;
    }

    let richTextObject = {
      relatedTextKey: adjustmentRowId,
      libraryItemId: matchingLibraryItemLink.libraryItemId,
      data: libraryItem.data.richTextData,
      childrenData: libraryItem.childrenData,
    };

    // eslint-disable-next-line no-restricted-syntax
    for (const i of libraryItem.childrenData) {
      switch (i.type) {
        case 301:
        case 302: {
          request = {
            ...i.data,
            groupedByKey: i.data.driveItemId,
            excelLibraryItems: [
              {
                id: i.data.id,
                type: i.type,
                data: {
                  formatType: 'HTML',
                  worksheetName: i.data.worksheetName,
                  uniqueId: i.data.uniqueId,
                  cellRange: i.type === 301 ? i.data.cellRange : i.data.name,
                  namedRange: i.type === 301 ? i.data.name : null,
                },
                childrenData: [],
              },
              {
                id: i.data.id,
                type: i.type,
                data: {
                  formatType: 'SVG',
                  worksheetName: i.data.worksheetName,
                  uniqueId: i.data.uniqueId,
                  cellRange: i.type === 301 ? i.data.cellRange : i.data.name,
                  namedRange: i.type === 301 ? i.data.name : null,
                },
                childrenData: [],
              },
            ],
          };

          try {
            // eslint-disable-next-line no-await-in-loop
            const { item } = await fetchItemAndAutosync(projectId, request);

            richTextObject = {
              ...richTextObject,
              data: libraryItem.data.richTextData,
              childrenData: [
                {
                  ...libraryItem.childrenData[0],
                  id: libraryItem.childrenData[0].data.id,
                  htmlData:
                    libraryItem.childrenData[0].data.formatType === 'SVG'
                      ? item.fileContents[1].value
                      : item.fileContents[0].value,
                  type: i.type,
                  notFound: false,
                },
              ],
            };

            if (item.fileContents[0].error) {
              const unableToLoad = stringifyUnableToLoadContent({
                ...i,
                placeId: 'content-error-302-301-longDescription',
                projectId,
                isPwcUser,
              });

              window.__LIBRARYITEMSTORE__.add({
                data: { formatType: 'HTML' },
                id: i.id,
                rendered: unableToLoad,
                formatType: 'HTML',
                type: i.type,
                contentType: i.type,
                error: item.fileContents[0].error,
                request,
              });
            }

            if (item && item.fileContents && item.fileContents[1].value)
              if (window && window.__LIBRARYITEMSTORE__) {
                const bopObject = {
                  id: i.id,
                  data: {
                    ...i.data,
                    worksheetName:
                      item.fileContents[0].autoSync.data.worksheetName,
                    sharePointItemUrl: item.fileName,
                  },
                  autoSync: item.fileContents[0].autoSync,
                  rendered: item.fileContents[0].value,
                  renderedHtml: item.fileContents[0].value,
                  renderedSvg: item.fileContents[1].value,
                  tempRenderSvg: item.fileContents[1].value,
                  type: i.type,
                  contentType: i.type,
                };

                window.__LIBRARYITEMSTORE__.add(bopObject);
              }
            break;
          } catch (err) {
            const unableToLoad = stringifyUnableToLoadContent({
              ...i,
              apiErrorCod: err.apiErrorCode,
              errorCode: err.errorCode,
              placeId: 'content-error-302-301-longDescription',
              projectId,
              isPwcUser,
            });

            window.__LIBRARYITEMSTORE__.add({
              data: { ...i.data, formatType: 'HTML' },
              id: i.id,
              rendered: unableToLoad,
              formatType: 'HTML',
              type: i.type,
              contentType: i.type,
              error: {
                isError: true,
                apiErrorType: 1,
                apiErrorCode: 'E1005-101',
                apiErrorMessage:
                  "One or more errors occurred. (Svg or HTML failed to be generated as the source file is deleted')",
                ...err,
                isSafeForClientViewing: false,
              },
              request,
            });
            break;
          }
        }
        case Constants.LIBRARY_ITEM_TYPES.EXCEL_CHART.value:
        case 300: {
          const requestChart = {
            ...i.data,
            groupedByKey: i.data.driveItemId,
            excelLibraryItems: [
              {
                id: i.id,
                name: i.name,
                type: i.type,
                data: {
                  formatType: 'SVG',
                  worksheetName: i.data.worksheetName,
                  uniqueId: i.data.uniqueId,
                  chartName: i.data.chartName,
                },
              },
            ],
          };
          try {
            // eslint-disable-next-line no-await-in-loop
            const { autoSync, item } = await fetchItemAndAutosync(
              projectId,
              requestChart
            );

            const innerChartdata = item?.fileContents[0];

            const excelPPTItem = {
              fileName: item.fileName,
              worksheetName:
                autoSync.data.worksheetName || i.data.worksheetName,
              libraryItemId: i.id,
              autoSync,
              type: i.type,
              chartName: autoSync.data.chartName || i.data.chartName,
            };

            const autosync = syncedInfoWrapped({
              projectId,
              excelPPTItem,
              contextStatusInfoItem: {
                isEditMode: true,
                isViewMode: false,
                shareWithAll: false,
              },
              isPwcUser,
            });

            const unableToLoad = stringifyUnableToLoadContent({
              ...i,
              placeId: 'content-error-300-longDescription',
              projectId,
              data: excelPPTItem,
              isPwcUser,
            });

            const chartItem = {
              type: i.type,
              id: i.id,
              libraryItemId: i.id,
              fileName: item.fileName,
              rendered: innerChartdata.error
                ? unableToLoad
                : `${innerChartdata.value}${autosync}`,
              renderedSvg: innerChartdata.error
                ? null
                : junctionColorMapping(innerChartdata.value, false, null, true),
              error: innerChartdata.error,
              autoSync,
            };
            window.__LIBRARYITEMSTORE__.add(chartItem);
            break;
          } catch (err) {
            // example err
            // {
            //   "apiErrorCode": "E1005-104",
            //   "apiErrorMessage": "Could not find Excel drive item",
            //   "errorCode": "E1005-102-104"
            // }
            const unableToLoad = stringifyUnableToLoadContent({
              ...i,
              apiErrorCode: err.apiErrorCode,
              errorCode: err.errorCode,
              placeId: 'content-error-300-catch-longDescription',
              projectId,
              isPwcUser,
            });

            window.__LIBRARYITEMSTORE__.add({
              data: i.data,
              id: i.id,
              rendered: unableToLoad,
              type: i.type,
              contentType: i.type,
              error: {
                isError: true,
                apiErrorType: 1,
                apiErrorMessage: 'File was removed',
                ...err,
              },
              request: requestChart,
            });
            break;
          }
        }
        case 500: {
          const requestPPT = {
            ...i.data,
            groupedByKey: i.data.driveItemId,
            pptLibraryItems: [
              {
                id: i.id,
                type: i.type,
                data: {
                  slideNumber: i.data.slideNumber,
                  formatType: 'SVG',
                  groupName: i.data.groupName,
                },
              },
            ],
          };

          try {
            // eslint-disable-next-line no-await-in-loop
            const pptData = await getPptLibraryItemsByGroup(
              projectId,
              requestPPT
            );

            const excelPPTItem = {
              groupName: requestPPT.groupName || '',
              libraryItemId: i.id,
              sharePointItemUrl: pptData.fileName,
              slideNumber: i.data.slideNumber,
              fileName: pptData.fileName,
              error: pptData.error,
              type: i.type,
            };
            const autosync = syncedInfoWrapped({
              projectId,
              excelPPTItem,
              contextStatusInfoItem: {
                isEditMode: true,
                isViewMode: false,
                shareWithAll: false,
              },
              isPwcUser,
            });
            window.__LIBRARYITEMSTORE__.add({
              id: i.id,
              data: {
                sharePointItemUrl: pptData.fileName,
                slideNumber: i.data.slideNumber,
              },
              rendered: `${pptData.fileContents[0].value}${autosync}`,
              type: i.type,
              contentType: i.type,
              error: pptData.fileContents[0].error,
              request: requestPPT,
            });
            break;
          } catch (err) {
            const unableToLoad = stringifyUnableToLoadContent({
              ...i,
              apiErrorCode: err.apiErrorCode,
              errorCode: err.errorCode,
              placeId: 'content-error-500-catch-longDescription',
              isPwcUser,
            });

            window.__LIBRARYITEMSTORE__.add({
              id: i.id,
              data: i.data,
              rendered: unableToLoad,
              type: i.type,
              contentType: i.type,
              error: { isError: true, ...err },
              request: requestPPT,
            });
            break;
          }
        }
        default: {
          window.__LIBRARYITEMSTORE__.add({
            id: i.id,
            renderedSvg: i.htmlData,
            htmlData: i.htmlData,
          });
        }
      }
    }

    return richTextObject;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('ERROR at getRichTextLongDescription: ', err);
    return null;
  }
};
